import {memo} from 'react'
import {useParams} from 'react-router-dom'
import { Divider, Input } from 'antd'
// import {useParams} from 'react-router-dom'
import cardStyles from './AboutCard.variation.module.scss'
import styles from './RequestForecast.module.scss'
import useStore from 'base/store'
import { PermissionType } from 'common/types'
import {useInputFocus} from 'common/hooks'
import {
  Block,
  Text,
  Modal,
  Button, IconButton,
  DotsIcon,
  Form, useForm, useWatch, FormItem, InputAmount,
} from 'common/components'
// import {useGetForecast} from 'common/api'
import { isEqual } from 'common/helpers'
import {FIELD_RULES} from 'common/constants'
import {VariationScheduleList} from '..'
import {useGetVariationRequest, useGetVariationSchedule, useSaveVariationFacilityLimit2} from '../api.variation'
// import { SCHEDULE_DATA_FACILITY } from './VariationScheduleList.mock'
import { 
  formatMoney,
  cbFilterSchedule, cbFilterJournal, cbMap2Schedule, cbMap2Journal 
} from '../schedule.helper'

const {TextArea} = Input

interface CardProps {
  isConfirm?: boolean
  balance: number
  request:any
  onModalOpen?: () => void
}

interface FormProps {
  isDuringConfirm: boolean
  isConfirm: boolean
  balance: number
  request: any
  onClose: () => void
}

export default function VariationRequestFacilityLimit2({
  isDuringConfirm, isConfirm, showCard
}:{
  isDuringConfirm?: boolean     // it is during the 2nd round right now
  isConfirm?: boolean           // this component is for the 2nd round tasks
  showCard?: boolean
}) {
  const params = useParams()
  const {data:request} = useGetVariationRequest(params.uuid)
  const {data} = useGetVariationSchedule(params.uuid, 'finalSchedule')

  const modalOpenKey = useStore((state) => state.variationFacilityLimit2OpenKey)
  const setModalOpenKey= useStore((state) => state.setVariationFacilityLimit2OpenKey)
  const handleModalOpen = () => setModalOpenKey(!isConfirm ? '1st':'2nd')
  const handleModalClose = () => setModalOpenKey(null)

  // console.log(data)
  const {balance} = data || {}
  const scheduleItems = data?.scheduleItems?.filter(
    cbFilterSchedule
  ).map(cbMap2Schedule) || []
  // const journalItems: any[] = []
  const journalItems = data?.scheduleItems?.filter(
    cbFilterJournal
  ).map(cbMap2Journal) || []

  return (
    <>
      { !!showCard &&
      <Block relative type="white">
        <Card isConfirm={isConfirm}
          balance={balance}
          request={request}
          onModalOpen={handleModalOpen}
        />
      </Block>
      }
      <Modal
        // title={`Variation Forecast`}
        width={1392}
        open={modalOpenKey === (!isConfirm ? '1st':'2nd') }
        onCancel={handleModalClose}
      >
        <Text mt={48} mb={16} size="2xl" weight="bold" color="black">Facility limit 2</Text>
        <Divider />
        <Text mb={60} size="md" color="black">
          The system has generated the schedule for the facility limit 2 calculation as follows. If needed, you can make a journal entry to adjust the balance based on the contract.
        </Text>

        <VariationScheduleList
          permissionRequired='edit variation fc2'
          statusRequired={ !isConfirm ? 'lending-manager-check':'ready-to-open'}
          title="Schedule for facility limit 2 calculation" 
          exTitle="facility-limit-2"
          request={request}
          data={[
            // ...SCHEDULE_DATA_ESTABLISHMENT_FEE,
            ...scheduleItems,
          ]}
          journal={[
            // ...SCHEDULE_DATA_JOURNAL_INPUT
            ...journalItems
          ]} 
        />
        <Divider />
        <ConfirmForm
          isDuringConfirm={isDuringConfirm}
          isConfirm= {isConfirm}
          balance={balance}
          request={request}
          onClose={handleModalClose}
        />
      </Modal>
    </>
  )
}

const Card = memo(
  ({balance, request, isConfirm, onModalOpen}: CardProps) => {
    // console.log(request)

    const org = balance ?? 0
    const fl2 = ( !isConfirm ? request?.facilityLimit2:request?.facilityLimit2Confirmed) ?? 0
    const dif = fl2 - org

    return (
      <>
        { !!onModalOpen &&
          <IconButton className={styles.edit} onClick={onModalOpen}>
            <DotsIcon />
          </IconButton>
        }

        <div className={cardStyles.root}>

          <Text size="md" weight="demiBold" color="black" mb={22}>
            Facility limit 2
          </Text>

          <div className={cardStyles['grid-columns-3']}>
            <div>
              <Text color="grayer" size="xs" mb={4}>System estimated</Text>
              <Text color="black"  size="md" weight="medium">{formatMoney(org)}</Text>
            </div>
            <div>
              <Text color="grayer" size="xs" mb={4}>Confirmed</Text>
              <Text color="black"  size="md" weight="medium">{formatMoney(fl2)}</Text>
            </div>
            <div>
              <Text color="grayer" size="xs" mb={4}>Differences</Text>
              <Text color="brown" size="md" weight="medium">
                {`${formatMoney(dif, '0')}`}
              </Text>
            </div>
          </div>

          <Text mt={12} mb={4} color="grayer" size="xs">Notes</Text>
          <Text color="black" size="xs" >
            {( !isConfirm ? request?.facilityLimit2Comment:request?.facilityLimit2CommentConfirmed) || ''}
          </Text>
        </div>
      </>
    )
  },
  (prevProps, nextProps) => isEqual(prevProps, nextProps)
)

const ConfirmForm = ({balance, request, isConfirm, isDuringConfirm, onClose}: FormProps) => {
  const permissions: PermissionType[] = useStore((state) => state.permissions)
  const {mutate: saveFL2} = useSaveVariationFacilityLimit2(request.uuid, onClose)

  const [form] = useForm()
  const inputRef = useInputFocus()

  const handleFinish = (values: any) => {
    // console.log(values)
    saveFL2({
      "facilityLimit2": values.facilityLimit2,
      "comment": values.comment
    })
  }

  const origin:number   = balance ?? 0
  const fcValue: number = useWatch('facilityLimit2', form) ?? 0
  const diff:number     = (fcValue ?? 0) - origin
  // console.log(request)

  const initialValues = {
    facilityLimit2: (!!isConfirm ? (request?.facilityLimit2Confirmed || request?.facilityLimit2):request?.facilityLimit2) || origin,
    comment: (!!isConfirm ? (request?.facilityLimit2CommentConfirmed || request?.facilityLimit2Comment):request?.facilityLimit2Comment) || '',
  }

  const statusRequired= !isConfirm ? 'lending-manager-check':'ready-to-open'
  const isNotAllowed = (statusRequired !== request?.status) || (!permissions?.includes('edit variation fc2'))
  const disableInput = (!isConfirm && !!isDuringConfirm) || !!isNotAllowed

  return (
    <>
      <Text mt={52} mb={40} size="lg" color='black' weight="bold">Facility limit 2</Text>
      <Form className={styles['confirm-form']}
        initialValues={initialValues}
        form={form}
        validateTrigger="onChange"
        // onValuesChange={handleFieldsChange}
        onFinish={handleFinish}
      >
        <Text mb={40} size="md" color='black'>
          Based on the provided input, the new facility limit 2 is
          &nbsp;
          <span className={styles['confirm-highlight']}>{formatMoney(origin, '0')}</span>,
          &nbsp;
          for your reference, If you want to adjust the facility limit, please add a journal entry accordingly.
        </Text>
        <div className={styles['flex-start']}>
          <div style={{width: '168px'}}>
            <FormItem name="facilityLimit2" label="Facility limit 2" rules={FIELD_RULES}>
              <InputAmount ref={inputRef} min={0} disabled={disableInput} />
            </FormItem>
          </div>
          <div style={{width: 'auto'}}>
            <Text mb={6} size="xs" color="grayer">Differences with the estimated value</Text>
            <div style={{height: '48px', display: 'flex', alignItems: 'center'}}>
              <Text size="md" color="brown" weight="medium">
                {formatMoney(diff)}
              </Text>
            </div>
          </div>
        </div>
        <div style={{width: '792px'}}>
          <FormItem name="comment" label="Comments" rules={FIELD_RULES}>
            <TextArea disabled={disableInput} rows={7} placeholder='Please enter comments for review...' />
          </FormItem>
        </div>
        
        <div className={styles['flex-center']}>
        { !disableInput &&
          <Button // block
            htmlType="submit"
            loading={
              false
              // isCreateMutating || isUpdateMutating || isUpdateForecastMutating
            }
          >
            Confirm
          </Button>
        }
        </div>
      </Form>
    </>
  )
}
